import React, {memo, useEffect, useState} from 'react';
import classNames from 'classnames';
import {useMediaQuery} from '@chakra-ui/react';
import {
  Col,
  UiBox,
  UiButton,
  UiCenter,
  UiContainer,
  UiIcon,
  UiImage,
  UiInputDatePicker,
  UiInputTimePicker,
  UiText,
} from '~/components/elements';
import {IoIosArrowBack} from 'react-icons/all';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch} from '~/redux/root-store';
import {QUERY_MOBILE} from '~/constants';
import moment from 'moment';
import TextModal from '~/components/modals/TextModal';
import {RootState} from '~/redux/root-reducer';
import {setAppointmentSorryModal, setAppointmentTimeSelectModal} from '~/redux/ui/slice';
import {DATE_FORMAT, LAST_DAY_NEXT_MONTH} from '~/utils/helpers';
import {fetchAppointmentDate} from '~/redux/questionnaires/thunk';
import {requestOK} from '~/utils/thunk-api';
import {unwrapResult} from '@reduxjs/toolkit';
import {useRequestState} from '~/hooks';

interface Props {
  backIcon?: boolean;
  children?: React.ReactNode;
  containerClasses?: string;
  handleOnNext?: () => void;
  handleGoBack?: () => void;
  buttonTittle?: string;
  timeSlotTitle?: string;
  disableNext?: boolean;
  isBack?: boolean;
  descriptions: string[];
  dateValue?: Date;
  timeValue?: Date;
  onChangeDate?: (value?: Date) => void;
  onChangeTime?: (value?: Date) => void;
  timeSelectionTitle?: string;
  timeList?: any;
  loading?: boolean;
  selectTimePlaceholder?: string;
  showEndTime?: boolean;
  note?: string;
  showNoSlotWantToBook?: boolean;
  isPlanBBooking?: boolean;
}

export const UiAppointmentBookingLayout = memo(function UiAppointmentBookingLayout({
  containerClasses,
  children,
  backIcon,
  handleOnNext,
  handleGoBack,
  buttonTittle = 'Continue',
  isBack = false,
  disableNext = false,
  timeSlotTitle,
  descriptions,
  dateValue,
  timeValue,
  onChangeDate,
  onChangeTime,
  timeSelectionTitle,
  timeList,
  selectTimePlaceholder = 'Select appointment Time',
  note,
  loading = false,
  showEndTime = false,
  showNoSlotWantToBook = false,
  isPlanBBooking = false,
  ...rest
}: Props) {
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`);
  const dispatch: AppDispatch = useDispatch();
  const isShowSorryModal = useSelector((state: RootState) => state.ui.appointmentSorryModal.isShowing);
  const userQuestionnaireId = useSelector((state: RootState) => state.questionnaires.userQuestionnaireId);
  const {userQuestionId} = useSelector((state: RootState) => state.questionnaires.rebookingAppointment);
  const {loading: fetchDateLoading} = useRequestState(fetchAppointmentDate);

  const [availableDate, setAvailableDate] = useState<any[]>();

  const isAvailableDate = (date: Date) => {
    const canBooking = availableDate?.find(i => moment(date).isSame(i.date, 'day'));
    return canBooking?.canBooking;
  };

  useEffect(() => {
    const getAvailableDates = async () => {
      const questionnaireId = userQuestionnaireId || userQuestionId;
      if (questionnaireId) {
        const resAction = await dispatch(
          fetchAppointmentDate({
            startTime: moment().format(DATE_FORMAT),
            endTime: LAST_DAY_NEXT_MONTH.format(DATE_FORMAT),
            userQuestionnaireId: questionnaireId,
          }),
        );
        if (requestOK(resAction, fetchAppointmentDate)) {
          const result = unwrapResult(resAction);
          setAvailableDate(result);
        }
      }
    };
    getAvailableDates();
  }, [dispatch, userQuestionId, userQuestionnaireId]);

  const onChangeDateCalendar = (value?: Date) => {
    if (value && onChangeDate && onChangeTime) {
      onChangeDate(value);
      onChangeTime(undefined);
    }
  };

  const nextScreen = () => {
    if (handleOnNext) {
      handleOnNext();
    }
  };

  const prevScreen = () => {
    if (handleGoBack) {
      handleGoBack();
    }
  };

  const handleSorryModal = () => {
    dispatch(setAppointmentSorryModal());
  };

  const openTimeSelectionModal = () => {
    dispatch(setAppointmentTimeSelectModal());
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <TextModal
        isShowing={isShowSorryModal}
        title={`We're sorry`}
        onDismissModal={handleSorryModal}
        buttonTitle="Select another slot"
        onClickSubmit={openTimeSelectionModal}
      >
        <UiText mb="15px">This slot is no longer available</UiText>
        <UiText>
          Someone else has booked this slot at the same time as you. Click the button below to select another slot.
        </UiText>
      </TextModal>
      <UiContainer maxW="container.md" height="100%" position="relative" p="20px" pt="5px" pb={0} {...rest}>
        {isBack && (
          <UiIcon
            as={IoIosArrowBack}
            boxSize={'20px'}
            mt={4}
            onClick={prevScreen}
            cursor="pointer"
            position="absolute"
          />
        )}
        <UiCenter>
          <UiImage type="icon" name="ease-logo" height="20px" width="92px" mt={4} />
        </UiCenter>

        <Col flex={1}>
          <Col
            id="qns-layout"
            className={classNames(`animate__animated animate__fadeInLeft`, containerClasses)}
            pb="20px"
            flex={1}
          >
            <Col alignItems="center" textAlign="center">
              <UiImage type="icon" name="calendar" boxSize="100px" mt="50px" />
              <UiText fontSize="lg" fontWeight="bold" mt="50px">
                Select an appointment time
              </UiText>
              <Col width="100%" alignItems="flex-start" mt="30px">
                <UiText fontWeight="600" mb="10px">
                  Select Date
                </UiText>
                <UiInputDatePicker
                  placeholder="Select appointment date"
                  headerTitle="Date Selection"
                  minDate={moment().toDate()}
                  maxDate={LAST_DAY_NEXT_MONTH.toDate()}
                  defaultValue={dateValue}
                  onChange={onChangeDateCalendar}
                  filterDate={isAvailableDate}
                  loading={fetchDateLoading}
                  showNoSlotWantToBook={showNoSlotWantToBook}
                  isPlanBBooking={isPlanBBooking}
                />
                <UiText fontWeight="600" mb="10px" mt="30px">
                  Select Time
                </UiText>
                <UiInputTimePicker
                  note={note}
                  showEndTime={showEndTime}
                  date={dateValue}
                  defaultValue={timeValue}
                  placeholder={selectTimePlaceholder}
                  headerTitle={timeSelectionTitle}
                  onChange={onChangeTime}
                  onChangeDate={onChangeDate}
                  showNoSlotWantToBook={showNoSlotWantToBook}
                  isPlanBBooking={isPlanBBooking}
                />
              </Col>

              {descriptions?.map((i: string) => (
                <UiText key={i} mt="25px" textAlign="center" color="liteOuter">
                  {i}
                </UiText>
              ))}
            </Col>
          </Col>
          <UiBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            pt={0}
            pb={10}
            w="100%"
            bg="primaryBackground"
          >
            <UiButton
              disabled={disableNext}
              isLoading={loading}
              onClick={nextScreen}
              title={buttonTittle}
              maxW="400px"
              w="100%"
              _hover={{
                background: 'baseColor',
                color: 'white',
              }}
              alignItems="center"
              justifyContent="center"
              display="flex"
            />
          </UiBox>
        </Col>
      </UiContainer>
    </>
  );
});
